import { useState, useEffect, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, CheckCircle2, Target, Clock, TrendingDown, Zap, ShieldAlert, Euro, Info, BarChart3, Repeat, Gauge, Crosshair, Lock, Layers, Users, Megaphone, FileCode2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── Types ───────────────────────────────────────────────────

/** Strategic lock-in categories that make a pivot structurally impossible */
type LockInType = 'architecture' | 'icp' | 'sales' | 'narrative' | 'team';

interface LockInOption {
  id: LockInType;
  label: string;
  description: string;
  example: string;
  icon: typeof Lock;
}

const LOCK_IN_OPTIONS: LockInOption[] = [
  {
    id: 'architecture',
    label: 'Architektur-Lock-In',
    description: 'Technische Festlegung, die einen Richtungswechsel architektonisch unmöglich macht.',
    example: 'z.B. On-Prem-Architektur live beim ersten Enterprise-Kunden',
    icon: FileCode2,
  },
  {
    id: 'icp',
    label: 'ICP-Lock-In',
    description: 'Kundensegment-Fixierung, die das Produkt für andere Zielgruppen unbrauchbar macht.',
    example: 'z.B. Dedicated Enterprise-Features in Core-Product integriert',
    icon: Users,
  },
  {
    id: 'sales',
    label: 'Sales-Lock-In',
    description: 'Vertriebsstruktur, die nicht mehr rückgängig gemacht werden kann.',
    example: 'z.B. 2 Enterprise-Deals signed mit Custom-Commitments',
    icon: Layers,
  },
  {
    id: 'narrative',
    label: 'Narrativ-Lock-In',
    description: 'Öffentliche Positionierung, die den Markt und Stakeholder festlegt.',
    example: 'z.B. Marketingpositionierung öffentlich angepasst, PR-Kampagne gestartet',
    icon: Megaphone,
  },
  {
    id: 'team',
    label: 'Team-Lock-In',
    description: 'Organisatorische Festlegung, die nicht mehr rückgängig gemacht werden kann.',
    example: 'z.B. Sales-Team komplett auf Enterprise umgestellt, Hiring abgeschlossen',
    icon: Users,
  },
];

export interface Phase13Data {
  validationMetric: string;
  validationThreshold: string;
  validationDeadlineDays: number;
  pivotDevMonths: number;
  pivotCalendarMonths: number;
  pivotCostEur: number;
  exposureUntilValidation: number;
  exposureUntilPivot: number;
  commitDecision: 'commit' | 'reduce_risk';
  // Masterclass fields
  expectedExposure?: number;
  adjustedPivotCost?: number;
  dailyExposure?: number;
  // Lock-in fields
  lockInTypes?: LockInType[];
  lockInConcreteStep?: string;
  structuralUrgencyScore?: number;
  structuralUrgencyLevel?: 'green' | 'yellow' | 'red';
}

interface Phase13IrreversibilityEngineProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: Phase13Data) => void;
  onGateStatusChange?: (valid: boolean) => void;
  thesisText?: string;
  killerCondition?: string;
  capacityPercent?: number;
  kpiMetric?: string;
  kpiFrom?: string;
  kpiTo?: string;
  monthlyCost?: number;
  totalCost?: number;
  wastedCapacityMonths?: number;
  teamCostPerMonth?: number;
  commitmentDecision?: string;
  optionALabel?: string;
  optionBLabel?: string;
  worstCase?: string;
  reversibility?: string;
  worstCaseProbability?: string;
  urgencyScore?: number;
  className?: string;
}

// ─── Helpers ─────────────────────────────────────────────────

const KPI_LABELS: Record<string, string> = {
  pipeline: "Pipeline / Leads",
  revenue: "Umsatz",
  time_to_market: "Time-to-Market",
  burn_rate: "Burn-Rate",
  market_share: "Marktanteil",
  learning_cycles: "Lernzyklen",
  dev_capacity: "Dev-Kapazität",
};

const REVERSIBILITY_LABELS: Record<string, string> = {
  irreversibel: "Irreversibel",
  schwer: "Schwer reversibel",
  mittel: "Mittel reversibel",
  leicht: "Leicht reversibel",
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function mapWorstCaseProb(prob?: string): number {
  switch (prob) {
    case 'hoch': return 0.85;
    case 'mittel': return 0.50;
    case 'gering': return 0.20;
    default: return 0.50;
  }
}

function mapWorstCaseProbLabel(prob?: string): string {
  switch (prob) {
    case 'hoch': return 'Hoch';
    case 'mittel': return 'Mittel';
    case 'gering': return 'Gering';
    default: return 'Mittel';
  }
}

function mapReversibilityMultiplier(rev?: string): number {
  switch (rev) {
    case 'irreversibel': return 1.5;
    case 'schwer': return 1.3;
    case 'mittel': return 1.0;
    case 'leicht': return 0.7;
    default: return 1.0;
  }
}

function mapProbBadgeVariant(prob?: string): "destructive" | "secondary" | "outline" {
  switch (prob) {
    case 'hoch': return 'destructive';
    case 'mittel': return 'secondary';
    case 'gering': return 'outline';
    default: return 'secondary';
  }
}

/**
 * Computed Structural Urgency
 * Replaces the cosmetic urgency slider with a calculated score based on:
 * - Inaction cost rate (how fast costs accumulate)
 * - Capacity lock-in rate (how much capacity is being consumed)
 * - PNR/Deadline ratio (how close PNR is relative to validation deadline)
 * - Number of lock-in dimensions affected
 */
function computeStructuralUrgency(params: {
  monthlyCost?: number;
  capacityPercent?: number;
  pivotCalendarMonths: number;
  deadlineDays: number;
  lockInCount: number;
  revMultiplier: number;
}): { score: number; level: 'green' | 'yellow' | 'red' } {
  const { monthlyCost, capacityPercent, pivotCalendarMonths, deadlineDays, lockInCount, revMultiplier } = params;
  const deadlineMonths = deadlineDays / 30;

  // Factor 1: PNR/Deadline ratio (0-40 points) — the core structural signal
  let pnrRatio = 0;
  if (pivotCalendarMonths > 0 && deadlineMonths > 0) {
    const ratio = deadlineMonths / pivotCalendarMonths;
    // ratio > 1 means validation takes longer than PNR → extremely dangerous
    // ratio = 1 means exactly at the edge
    // ratio < 0.5 means lots of headroom
    if (ratio >= 1) pnrRatio = 40;
    else if (ratio >= 0.75) pnrRatio = 30;
    else if (ratio >= 0.5) pnrRatio = 20;
    else pnrRatio = 10;
  }

  // Factor 2: Capacity lock-in rate (0-20 points)
  const capScore = Math.min(20, ((capacityPercent || 0) / 100) * 20);

  // Factor 3: Inaction cost intensity (0-20 points)
  let costScore = 0;
  if (monthlyCost && monthlyCost > 0) {
    if (monthlyCost >= 50000) costScore = 20;
    else if (monthlyCost >= 20000) costScore = 15;
    else if (monthlyCost >= 10000) costScore = 10;
    else if (monthlyCost >= 5000) costScore = 5;
  }

  // Factor 4: Lock-in breadth (0-15 points) — more dimensions = harder to pivot
  const lockInScore = Math.min(15, lockInCount * 3);

  // Factor 5: Reversibility penalty (0-5 points)
  const revScore = revMultiplier >= 1.5 ? 5 : revMultiplier >= 1.3 ? 3 : 0;

  const total = Math.min(100, pnrRatio + capScore + costScore + lockInScore + revScore);
  const normalizedScore = Math.round(total / 10); // 0-10

  let level: 'green' | 'yellow' | 'red';
  if (total >= 60) level = 'red';
  else if (total >= 35) level = 'yellow';
  else level = 'green';

  return { score: normalizedScore, level };
}

// ─── Component ───────────────────────────────────────────────

export const Phase13IrreversibilityEngine = ({
  onValueChange,
  onStructuredChange,
  onGateStatusChange,
  thesisText,
  killerCondition,
  capacityPercent,
  kpiMetric,
  kpiFrom,
  kpiTo,
  monthlyCost,
  totalCost,
  wastedCapacityMonths,
  teamCostPerMonth = 8000,
  commitmentDecision: prevCommitment,
  optionALabel,
  optionBLabel,
  worstCase,
  reversibility,
  worstCaseProbability,
  urgencyScore,
  className,
}: Phase13IrreversibilityEngineProps) => {
  // ─── State ─────────────────────────────────────────────────
  const [validationMetric, setValidationMetric] = useState("");
  const [validationThreshold, setValidationThreshold] = useState("");
  const [deadlineValue, setDeadlineValue] = useState(90);
  const [deadlineUnit, setDeadlineUnit] = useState<'days' | 'weeks' | 'months'>('days');
  const [pivotDevMonths, setPivotDevMonths] = useState(6);
  const [commitDecision, setCommitDecision] = useState<'commit' | 'reduce_risk' | null>(null);
  const [commitCheckboxConfirmed, setCommitCheckboxConfirmed] = useState(false);

  // Lock-In state
  const [selectedLockIns, setSelectedLockIns] = useState<LockInType[]>([]);
  const [lockInConcreteStep, setLockInConcreteStep] = useState("");

  // Reset checkbox when decision changes
  useEffect(() => {
    setCommitCheckboxConfirmed(false);
  }, [commitDecision]);

  // ─── Derived: deadline in days ────────────────────────────
  const deadlineDays = useMemo(() => {
    switch (deadlineUnit) {
      case 'weeks': return deadlineValue * 7;
      case 'months': return deadlineValue * 30;
      default: return deadlineValue;
    }
  }, [deadlineValue, deadlineUnit]);

  // ─── Derived: KPI ─────────────────────────────────────────
  const kpiDelta = useMemo(() => {
    const from = parseFloat(kpiFrom || '0');
    const to = parseFloat(kpiTo || '0');
    return Math.abs(from - to);
  }, [kpiFrom, kpiTo]);
  const kpiLabel = kpiMetric ? (KPI_LABELS[kpiMetric] || kpiMetric) : "KPI";
  const cap = capacityPercent || 0;

  // ─── Probability & Reversibility factors ──────────────────
  const worstCaseProbFactor = useMemo(() => mapWorstCaseProb(worstCaseProbability), [worstCaseProbability]);
  const revMultiplier = useMemo(() => mapReversibilityMultiplier(reversibility), [reversibility]);

  // ─── Block 2: Exposure until validation ───────────────────
  const exposureUntilValidation = useMemo(() => {
    if (!monthlyCost || monthlyCost <= 0) return 0;
    return (deadlineDays / 30) * monthlyCost;
  }, [deadlineDays, monthlyCost]);

  const expectedExposure = useMemo(() => {
    return exposureUntilValidation * worstCaseProbFactor;
  }, [exposureUntilValidation, worstCaseProbFactor]);

  const dailyExposure = useMemo(() => {
    if (!monthlyCost || monthlyCost <= 0) return 0;
    return monthlyCost / 30;
  }, [monthlyCost]);

  // ─── Block 3: Pivot calculations ─────────────────────────
  const pivotCalendarMonths = useMemo(() => {
    if (cap <= 0) return pivotDevMonths;
    return pivotDevMonths / (cap / 100);
  }, [pivotDevMonths, cap]);

  const pivotCostEur = useMemo(() => {
    if (!monthlyCost || monthlyCost <= 0) return 0;
    return pivotCalendarMonths * monthlyCost;
  }, [pivotCalendarMonths, monthlyCost]);

  const capacityCostUntilPivot = useMemo(() => {
    return pivotCalendarMonths * teamCostPerMonth * (cap / 100);
  }, [pivotCalendarMonths, teamCostPerMonth, cap]);

  const totalExposureUntilPivot = useMemo(() => {
    return pivotCostEur + capacityCostUntilPivot;
  }, [pivotCostEur, capacityCostUntilPivot]);

  const adjustedPivotCost = useMemo(() => {
    return totalExposureUntilPivot * revMultiplier;
  }, [totalExposureUntilPivot, revMultiplier]);

  const expectedPivotExposure = useMemo(() => {
    return adjustedPivotCost * worstCaseProbFactor;
  }, [adjustedPivotCost, worstCaseProbFactor]);

  const dailyPivotBurn = useMemo(() => {
    if (pivotCalendarMonths <= 0) return 0;
    return adjustedPivotCost / (pivotCalendarMonths * 30);
  }, [adjustedPivotCost, pivotCalendarMonths]);

  const kpiLossUntilPivot = useMemo(() => kpiDelta * pivotCalendarMonths, [kpiDelta, pivotCalendarMonths]);

  // ─── Break-even ratio ────────────────────────────────────
  const validationToExposureRatio = useMemo(() => {
    if (adjustedPivotCost <= 0 || exposureUntilValidation <= 0) return 0;
    return (exposureUntilValidation / adjustedPivotCost) * 100;
  }, [exposureUntilValidation, adjustedPivotCost]);

  const pivotToInactionMultiple = useMemo(() => {
    if (!totalCost || totalCost <= 0 || adjustedPivotCost <= 0) return 0;
    return adjustedPivotCost / totalCost;
  }, [totalCost, adjustedPivotCost]);

  // ─── PNR vs Deadline consistency ─────────────────────────
  const deadlineMonths = useMemo(() => deadlineDays / 30, [deadlineDays]);
  const pnrRatio = useMemo(() => {
    if (pivotCalendarMonths <= 0) return 0;
    return deadlineMonths / pivotCalendarMonths;
  }, [deadlineMonths, pivotCalendarMonths]);

  const pnrStatus = useMemo((): 'critical' | 'warning' | 'safe' => {
    if (pnrRatio >= 1) return 'critical';  // PNR before validation completes
    if (pnrRatio >= 0.7) return 'warning'; // tight
    return 'safe';
  }, [pnrRatio]);

  const validationExceedsPivot = pnrStatus === 'critical';

  // ─── Computed Structural Urgency ─────────────────────────
  const structuralUrgency = useMemo(() => {
    return computeStructuralUrgency({
      monthlyCost,
      capacityPercent,
      pivotCalendarMonths,
      deadlineDays,
      lockInCount: selectedLockIns.length,
      revMultiplier,
    });
  }, [monthlyCost, capacityPercent, pivotCalendarMonths, deadlineDays, selectedLockIns.length, revMultiplier]);

  // Pivot probability estimate based on structural urgency
  const pivotProbability = useMemo(() => {
    // Inverse of urgency — more structural lock-in = lower pivot probability
    const base = 100 - structuralUrgency.score * 10;
    // Capacity lock-in further reduces pivot probability
    const capPenalty = Math.min(20, (cap / 100) * 20);
    // Lock-in breadth penalty
    const lockInPenalty = selectedLockIns.length * 5;
    return Math.max(5, Math.round(base - capPenalty - lockInPenalty));
  }, [structuralUrgency.score, cap, selectedLockIns.length]);

  // ─── Urgency-Deadline warning (from onboarding score) ────
  const urgencyDeadlineWarning = useMemo(() => {
    if (!urgencyScore) return null;
    if (urgencyScore >= 9 && deadlineDays > 60) return 'critical';
    if (urgencyScore >= 8 && deadlineDays > 90) return 'warning';
    return null;
  }, [urgencyScore, deadlineDays]);

  // ─── Lock-In toggle ──────────────────────────────────────
  const toggleLockIn = useCallback((id: LockInType) => {
    setSelectedLockIns(prev =>
      prev.includes(id)
        ? prev.filter(l => l !== id)
        : [...prev, id]
    );
  }, []);

  // ─── Validation ───────────────────────────────────────────
  const isMetricValid = validationMetric.trim().length >= 5;
  const isThresholdValid = validationThreshold.trim().length >= 1;
  const isDeadlineValid = deadlineDays >= 7 && deadlineDays <= 365;
  const isLockInValid = selectedLockIns.length >= 1 && lockInConcreteStep.trim().length >= 10;
  const allFieldsValid = isMetricValid && isThresholdValid && isDeadlineValid && isLockInValid;

  // ─── Gate logic ───────────────────────────────────────────
  useEffect(() => {
    if (!onGateStatusChange) return;
    onGateStatusChange(allFieldsValid && commitDecision !== null && commitCheckboxConfirmed);
  }, [allFieldsValid, commitDecision, commitCheckboxConfirmed, onGateStatusChange]);

  // ─── Output ───────────────────────────────────────────────
  const buildOutput = useCallback(() => {
    if (!allFieldsValid || !commitDecision || !commitCheckboxConfirmed) return;

    const data: Phase13Data = {
      validationMetric: validationMetric.trim(),
      validationThreshold: validationThreshold.trim(),
      validationDeadlineDays: deadlineDays,
      pivotDevMonths,
      pivotCalendarMonths,
      pivotCostEur,
      exposureUntilValidation,
      exposureUntilPivot: adjustedPivotCost,
      commitDecision,
      expectedExposure,
      adjustedPivotCost,
      dailyExposure,
      lockInTypes: selectedLockIns,
      lockInConcreteStep: lockInConcreteStep.trim(),
      structuralUrgencyScore: structuralUrgency.score,
      structuralUrgencyLevel: structuralUrgency.level,
    };

    onStructuredChange?.(data);

    const parts = [
      `Messgröße: ${validationMetric.trim()}`,
      `Mindestwert: ${validationThreshold.trim()}`,
      `Deadline: ${deadlineDays} Tage`,
      `Lock-In-Dimensionen: ${selectedLockIns.map(l => LOCK_IN_OPTIONS.find(o => o.id === l)?.label).join(', ')}`,
      `Konkreter Lock-In-Schritt: ${lockInConcreteStep.trim()}`,
      `Point of No Return: ${pivotCalendarMonths.toFixed(1)} Monate (${pivotDevMonths} Dev-Monate)`,
      `Strukturelle Dringlichkeit: ${structuralUrgency.score}/10 (${structuralUrgency.level})`,
      `Pivot-Wahrscheinlichkeit: ${pivotProbability}%`,
      `Exposition bis Validierung: ${formatCurrency(exposureUntilValidation)}`,
      `Wahrscheinlichkeitsgewichtet: ${formatCurrency(expectedExposure)}`,
      `Bereinigter Pivot-Schaden: ${formatCurrency(adjustedPivotCost)} (Rev.-Faktor ${revMultiplier}x)`,
      `Tägliche Exposition: ${formatCurrency(dailyExposure)}`,
      `Entscheidung: ${commitDecision === 'commit' ? 'Commitment' : 'Risiko reduzieren'}`,
    ];
    onValueChange(parts.join('\n'));
  }, [allFieldsValid, commitDecision, commitCheckboxConfirmed, validationMetric, validationThreshold, deadlineDays, pivotDevMonths, pivotCalendarMonths, pivotCostEur, exposureUntilValidation, adjustedPivotCost, expectedExposure, dailyExposure, revMultiplier, selectedLockIns, lockInConcreteStep, structuralUrgency, pivotProbability, onValueChange, onStructuredChange]);

  useEffect(() => {
    buildOutput();
  }, [buildOutput]);

  // ─── Urgency color helpers ────────────────────────────────
  const urgencyBg = structuralUrgency.level === 'red'
    ? 'bg-destructive/10 border-destructive/40'
    : structuralUrgency.level === 'yellow'
      ? 'bg-amber-500/10 border-amber-500/40'
      : 'bg-emerald-500/10 border-emerald-500/40';

  const urgencyText = structuralUrgency.level === 'red'
    ? 'text-destructive'
    : structuralUrgency.level === 'yellow'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-emerald-600 dark:text-emerald-400';

  const urgencyBarClass = structuralUrgency.level === 'red'
    ? '[&>div]:bg-destructive'
    : structuralUrgency.level === 'yellow'
      ? '[&>div]:bg-amber-500'
      : '[&>div]:bg-emerald-500';

  return (
    <div className={cn("space-y-6", className)}>
      {/* ═══ BLOCK 1: System Condensation – Full Strategic Mirror ═══ */}
      <div className="p-5 bg-muted/30 border border-border/40 rounded-xl space-y-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Eure Strategie steht und fällt mit folgender Annahme
            </p>
            {thesisText && (
              <p className="text-base font-medium text-foreground leading-relaxed">
                „{thesisText}"
              </p>
            )}
          </div>
        </div>

        {/* Option Labels */}
        {(optionALabel || optionBLabel) && (
          <div className="flex items-center gap-2 pt-2 border-t border-border/30">
            <Crosshair className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{optionALabel || 'Option A'}</span>
              {" vs. "}
              <span className="font-medium text-foreground">{optionBLabel || 'Option B'}</span>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border/30">
          {killerCondition && (
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Kipppunkt:</span> {killerCondition}
              </span>
            </div>
          )}
          {monthlyCost != null && monthlyCost > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <Euro className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Monatl. Kosten des Wartens:</span>{" "}
                {formatCurrency(monthlyCost)}
              </span>
            </div>
          )}
          {totalCost != null && totalCost > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <BarChart3 className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Kumulierte Inaction-Kosten:</span>{" "}
                {formatCurrency(totalCost)}
              </span>
            </div>
          )}
          {cap > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <Clock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Kapazitätsbindung:</span> {cap}%
              </span>
            </div>
          )}
          {wastedCapacityMonths != null && wastedCapacityMonths > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <TrendingDown className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Verschwendete Kapazität:</span>{" "}
                {wastedCapacityMonths} Dev-Monate
              </span>
            </div>
          )}
          {prevCommitment && (
            <div className="flex items-start gap-2 text-sm">
              <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Phase 1.2 Entscheidung:</span>{" "}
                {prevCommitment === 'commit' ? 'Commitment' : 'Bewusstes Warten'}
              </span>
            </div>
          )}
          {worstCase && (
            <div className="flex items-start gap-2 text-sm col-span-1 sm:col-span-2">
              <ShieldAlert className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">Worst Case:</span> {worstCase}
              </span>
            </div>
          )}
        </div>

        {/* Worst-Case Probability + Reversibility row */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/30">
          {worstCaseProbability && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Eintrittswahrscheinlichkeit:</span>
              <Badge variant={mapProbBadgeVariant(worstCaseProbability)}>
                {mapWorstCaseProbLabel(worstCaseProbability)}
              </Badge>
            </div>
          )}
          {reversibility && (
            <div className="flex items-center gap-1.5">
              <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Reversibilität:</span>
              <Badge variant={reversibility === 'irreversibel' || reversibility === 'schwer' ? 'destructive' : 'secondary'}>
                {REVERSIBILITY_LABELS[reversibility] || reversibility}
              </Badge>
            </div>
          )}
          {/* Onboarding urgency as reference only */}
          {urgencyScore != null && urgencyScore > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Onboarding-Dringlichkeit: {urgencyScore}/10</span>
            </div>
          )}
        </div>
      </div>

      {/* ═══ BLOCK 2: Messbare Kernannahme ═══ */}
      <div className="p-5 rounded-xl border-2 border-border bg-card/50 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <Label className="text-base font-semibold text-foreground block">
              Messbare Kernannahme definieren
            </Label>
            <span className="text-sm text-muted-foreground">
              Was muss eintreten, damit eure Strategie aufgeht?
            </span>
          </div>
        </div>

        {/* Metric */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">
            Messgröße <span className="text-destructive">*</span>
          </Label>
          <Input
            value={validationMetric}
            onChange={(e) => setValidationMetric(e.target.value)}
            placeholder="z.B. unterschriebene Enterprise-Deals"
            className="text-base"
          />
          {validationMetric.length > 0 && !isMetricValid && (
            <p className="text-xs text-destructive">Mindestens 5 Zeichen</p>
          )}
        </div>

        {/* Threshold */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">
            Mindestwert <span className="text-destructive">*</span>
          </Label>
          <Input
            value={validationThreshold}
            onChange={(e) => setValidationThreshold(e.target.value)}
            placeholder="z.B. >= 2 Deals"
            className="text-base"
          />
        </div>

        {/* Deadline */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">
            Deadline <span className="text-destructive">*</span>
          </Label>
          <div className="flex gap-3">
            <Input
              type="number"
              value={deadlineValue}
              onChange={(e) => setDeadlineValue(Math.max(1, parseInt(e.target.value) || 1))}
              className="text-base w-24"
              min={1}
            />
            <Select value={deadlineUnit} onValueChange={(v) => setDeadlineUnit(v as 'days' | 'weeks' | 'months')}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="days">Tage</SelectItem>
                <SelectItem value="weeks">Wochen</SelectItem>
                <SelectItem value="months">Monate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isDeadlineValid && deadlineDays > 0 && (
            <p className="text-xs text-destructive">
              {deadlineDays < 7 ? "Minimum 7 Tage" : "Maximum 365 Tage"}
            </p>
          )}
        </div>

        {/* Urgency-Deadline Warning */}
        {urgencyDeadlineWarning && (
          <div className={cn(
            "p-3 rounded-lg border text-sm animate-in fade-in duration-200",
            urgencyDeadlineWarning === 'critical'
              ? "bg-destructive/10 border-destructive/30 text-destructive"
              : "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300"
          )}>
            <div className="flex items-start gap-2">
              <Gauge className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">
                  Eure Dringlichkeit liegt bei {urgencyScore}/10, aber eure Validierungsdeadline beträgt {deadlineDays} Tage.
                </p>
                <p className="mt-1 text-muted-foreground">
                  {urgencyDeadlineWarning === 'critical'
                    ? `Bei dieser Dringlichkeit kostet euch jeder Tag ${dailyExposure > 0 ? formatCurrency(dailyExposure) : 'wertvolle Zeit'}. Könnt ihr euch ${deadlineDays} Tage leisten?`
                    : "Könnt ihr euch diesen Zeitraum bei eurer Dringlichkeit leisten?"
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Exposure until validation + Expected Exposure + Daily Cost */}
        {exposureUntilValidation > 0 && allFieldsValid && (
          <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                Bis zur Validierung seid ihr {formatCurrency(exposureUntilValidation)} exponiert.
              </p>
            </div>
            {worstCaseProbability && (
              <p className="text-sm text-amber-700/80 dark:text-amber-300/80 pl-6">
                Wahrscheinlichkeitsgewichtet: {formatCurrency(expectedExposure)}{" "}
                <span className="text-xs">(bei {mapWorstCaseProbLabel(worstCaseProbability).toLowerCase()}er Eintrittswahrscheinlichkeit)</span>
              </p>
            )}
            {dailyExposure > 0 && (
              <p className="text-sm text-amber-700/80 dark:text-amber-300/80 pl-6">
                Das sind {formatCurrency(dailyExposure)} pro Tag — jeder Tag ohne Validierung kostet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ═══ BLOCK 3: Strategic Lock-In Dimensions ═══ */}
      <div className="p-5 rounded-xl border-2 border-primary/20 bg-primary/5 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
            <Lock className="w-5 h-5 text-primary" />
          </div>
          <div>
            <Label className="text-base font-semibold text-foreground block">
              Strategische Lock-In-Dimensionen
            </Label>
            <span className="text-sm text-muted-foreground">
              Welche Festlegungen machen einen Pivot strukturell unmöglich?
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {LOCK_IN_OPTIONS.map((option) => {
            const isSelected = selectedLockIns.includes(option.id);
            const IconComp = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggleLockIn(option.id)}
                className={cn(
                  "w-full text-left p-4 rounded-lg border-2 transition-all duration-200",
                  isSelected
                    ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                    : "border-border/50 bg-card/50 hover:border-primary/30 hover:bg-primary/5"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "p-1.5 rounded-md transition-colors",
                    isSelected ? "bg-primary/20" : "bg-muted/50"
                  )}>
                    <IconComp className={cn("w-4 h-4", isSelected ? "text-primary" : "text-muted-foreground")} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn("font-semibold text-sm", isSelected ? "text-foreground" : "text-foreground/80")}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {option.description}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-0.5 italic">
                      {option.example}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {selectedLockIns.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-border/30">
            <Label className="text-sm font-medium text-foreground">
              Welcher konkrete Schritt macht den Pivot praktisch unmöglich? <span className="text-destructive">*</span>
            </Label>
            <Input
              value={lockInConcreteStep}
              onChange={(e) => setLockInConcreteStep(e.target.value)}
              placeholder="z.B. On-Prem-Architektur live beim ersten Enterprise-Kunden"
              className="text-base"
            />
            {lockInConcreteStep.length > 0 && lockInConcreteStep.trim().length < 10 && (
              <p className="text-xs text-destructive">Mindestens 10 Zeichen — seid konkret.</p>
            )}
          </div>
        )}

        {selectedLockIns.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Wählt mindestens eine Lock-In-Dimension, die euren Pivot strukturell einschränkt.
          </p>
        )}
      </div>

      {/* ═══ BLOCK 4: Point-of-No-Return Rechner ═══ */}
      <div className="p-5 rounded-xl border-2 border-destructive/20 bg-destructive/5 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-destructive/15 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <Label className="text-base font-semibold text-foreground block">
              Point of No Return
            </Label>
            <span className="text-sm text-muted-foreground">
              Ab wie vielen gebundenen Entwickler-Monaten ist ein Pivot nicht mehr möglich?
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">{pivotDevMonths} Dev-Monate</span>
            <span className="text-xs text-muted-foreground">1 – 24</span>
          </div>
          <Slider
            value={[pivotDevMonths]}
            onValueChange={([v]) => setPivotDevMonths(v)}
            min={1}
            max={24}
            step={1}
          />
        </div>

        {/* Calculated results */}
        <div className="p-4 rounded-lg border border-destructive/30 bg-background space-y-2">
          <p className="text-base font-semibold text-destructive">
            Ihr erreicht den Point of No Return in {pivotCalendarMonths.toFixed(1)} Monaten.
          </p>
          {adjustedPivotCost > 0 && (
            <p className="text-sm text-foreground">
              Das entspricht <span className="font-semibold text-destructive">{formatCurrency(adjustedPivotCost)}</span> Opportunitätskosten
              {kpiLossUntilPivot > 0 && (
                <> und <span className="font-semibold text-destructive">{kpiLossUntilPivot.toFixed(0)}</span> verlorene {kpiLabel}</>
              )}.
            </p>
          )}
          {dailyPivotBurn > 0 && (
            <p className="text-sm text-muted-foreground">
              Täglicher Burn bis zum Point of No Return: {formatCurrency(dailyPivotBurn)}
            </p>
          )}
        </div>

        {/* Reversibility multiplier info */}
        {reversibility && revMultiplier !== 1.0 && adjustedPivotCost > 0 && (
          <div className="p-3 rounded-lg border border-border/50 bg-muted/30">
            <div className="flex items-start gap-2 text-sm">
              <Repeat className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-foreground">
                  Euer Onboarding stuft diese Entscheidung als{" "}
                  <span className="font-semibold">„{REVERSIBILITY_LABELS[reversibility] || reversibility}"</span>{" "}
                  ein.
                </p>
                <p className="text-muted-foreground mt-0.5">
                  Bereinigter Pivot-Schaden: {formatCurrency(adjustedPivotCost)} (Faktor {revMultiplier}x)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── PNR vs Deadline Consistency Check ─────────────── */}
        {isDeadlineValid && (
          <div className={cn(
            "p-4 rounded-lg border-2 space-y-2",
            pnrStatus === 'critical'
              ? "border-destructive/50 bg-destructive/10"
              : pnrStatus === 'warning'
                ? "border-amber-500/40 bg-amber-500/10"
                : "border-emerald-500/40 bg-emerald-500/10"
          )}>
            <div className="flex items-start gap-3">
              {pnrStatus === 'critical' ? (
                <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              ) : pnrStatus === 'warning' ? (
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                {pnrStatus === 'critical' && (
                  <>
                    <p className="text-base font-bold text-destructive">
                      Eure Strategie wird irreversibel, bevor sie validiert ist.
                    </p>
                    <p className="text-sm text-foreground">
                      Validierung: {deadlineMonths.toFixed(1)} Monate · PNR: {pivotCalendarMonths.toFixed(1)} Monate
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ihr baut schneller Pivot-Schaden auf als Marktvalidierung. Wenn eure Annahme falsch ist, erkennt ihr es erst, wenn ein Kurswechsel nicht mehr möglich ist.
                    </p>
                  </>
                )}
                {pnrStatus === 'warning' && (
                  <>
                    <p className="text-base font-semibold text-amber-600 dark:text-amber-400">
                      Euer Validierungsfenster ist knapp.
                    </p>
                    <p className="text-sm text-foreground">
                      Validierung: {deadlineMonths.toFixed(1)} Monate · PNR: {pivotCalendarMonths.toFixed(1)} Monate
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Eure Validierung nutzt {(pnrRatio * 100).toFixed(0)}% eures strategischen Spielraums. Jede Verzögerung verschiebt euch in die Irreversibilitätszone.
                    </p>
                  </>
                )}
                {pnrStatus === 'safe' && (
                  <>
                    <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400">
                      Strategischer Spielraum vorhanden.
                    </p>
                    <p className="text-sm text-foreground">
                      Validierung: {deadlineMonths.toFixed(1)} Monate · PNR: {pivotCalendarMonths.toFixed(1)} Monate
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ihr habt {((1 - pnrRatio) * pivotCalendarMonths).toFixed(1)} Monate Puffer zwischen Validierung und Point of No Return.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Validation-to-Pivot ratio */}
        {validationToExposureRatio > 0 && allFieldsValid && (
          <div className={cn(
            "p-3 rounded-lg border text-sm",
            validationToExposureRatio > 50
              ? "border-destructive/40 bg-destructive/10"
              : "border-border/50 bg-muted/30"
          )}>
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-foreground">
                  Bis zur Validierung habt ihr bereits{" "}
                  <span className="font-semibold">{validationToExposureRatio.toFixed(0)}%</span>{" "}
                  des Gesamtschadens exponiert.
                </p>
                {validationToExposureRatio > 50 && (
                  <p className="text-destructive mt-1 font-medium">
                    Mehr als die Hälfte eures Pivot-Budgets ist allein für die Validierung gebunden.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pivot vs. Inaction cost comparison */}
        {pivotToInactionMultiple > 0 && (
          <div className={cn(
            "p-3 rounded-lg border text-sm",
            pivotToInactionMultiple > 5
              ? "border-destructive/40 bg-destructive/10"
              : "border-border/50 bg-muted/30"
          )}>
            <div className="flex items-start gap-2">
              <BarChart3 className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-foreground">
                  Phase 1.2.2 hat {formatCurrency(totalCost!)} Inaction-Kosten berechnet.
                  Euer Pivot-Schaden ist{" "}
                  <span className={cn("font-semibold", pivotToInactionMultiple > 5 ? "text-destructive" : "text-foreground")}>
                    {pivotToInactionMultiple.toFixed(1)}x höher
                  </span>.
                </p>
                {pivotToInactionMultiple > 5 && (
                  <p className="text-destructive mt-1 font-medium">
                    Ihr baut aktuell schneller Pivot-Schaden auf als Marktvalidierung.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══ BLOCK 5: Computed Structural Urgency ═══ */}
      <div className={cn("p-5 rounded-xl border-2 space-y-4", urgencyBg)}>
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-full flex items-center justify-center",
            structuralUrgency.level === 'red' ? "bg-destructive/15" :
            structuralUrgency.level === 'yellow' ? "bg-amber-500/15" : "bg-emerald-500/15"
          )}>
            <Gauge className={cn("w-5 h-5", urgencyText)} />
          </div>
          <div>
            <Label className="text-base font-semibold text-foreground block">
              Berechnete strukturelle Dringlichkeit
            </Label>
            <span className="text-xs text-muted-foreground">
              Systemberechnung auf Basis aller erfassten Daten — kein subjektiver Wert.
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={cn("text-2xl font-bold", urgencyText)}>
              {structuralUrgency.score}/10
            </span>
            <Badge variant={structuralUrgency.level === 'red' ? 'destructive' : structuralUrgency.level === 'yellow' ? 'secondary' : 'outline'}>
              {structuralUrgency.level === 'red' ? 'Lock-In vor Validierung' :
               structuralUrgency.level === 'yellow' ? 'Knappes Fenster' :
               'Strategischer Spielraum'}
            </Badge>
          </div>
          <Progress
            value={structuralUrgency.score * 10}
            className={cn("h-3", urgencyBarClass)}
            aria-label={`Strukturelle Dringlichkeit ${structuralUrgency.score} von 10`}
          />
        </div>

        {/* System assessment */}
        {selectedLockIns.length > 0 && cap > 0 && (
          <div className={cn("p-4 rounded-lg border bg-background space-y-2",
            structuralUrgency.level === 'red' ? "border-destructive/30" :
            structuralUrgency.level === 'yellow' ? "border-amber-500/30" : "border-emerald-500/30"
          )}>
            <p className={cn("text-sm font-semibold", urgencyText)}>
              System erkennt:
            </p>
            <p className="text-sm text-foreground">
              Wenn ihr {cap}% Kapazität {pivotDevMonths} Monate bindet
              {selectedLockIns.length > 0 && (
                <> und {selectedLockIns.length} Lock-In-Dimension{selectedLockIns.length > 1 ? 'en' : ''} aktiviert</>
              )},
              {structuralUrgency.level === 'red' ? (
                <> seid ihr <span className="font-bold text-destructive">strukturell auf eure aktuelle Strategie festgelegt</span>.</>
              ) : structuralUrgency.level === 'yellow' ? (
                <> wird ein Pivot <span className="font-semibold text-amber-600 dark:text-amber-400">zunehmend schwierig</span>.</>
              ) : (
                <> behaltet ihr <span className="font-semibold text-emerald-600 dark:text-emerald-400">strategische Flexibilität</span>.</>
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              Pivot-Wahrscheinlichkeit sinkt auf ca. <span className="font-semibold text-foreground">{pivotProbability}%</span>.
            </p>

            {/* The killer question */}
            {structuralUrgency.level !== 'green' && (
              <div className="pt-2 mt-2 border-t border-border/30">
                <p className={cn("text-sm font-bold", structuralUrgency.level === 'red' ? "text-destructive" : "text-amber-600 dark:text-amber-400")}>
                  Wollt ihr diese Irreversibilität VOR oder NACH Marktvalidierung?
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ BLOCK 6: Konsequenz-Statement + Checkbox Gate ═══ */}
      {allFieldsValid && (
        <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {/* Consequence statement */}
          <div className="p-5 rounded-xl border-2 border-destructive/30 bg-destructive/5">
            <p className="text-sm font-semibold text-destructive uppercase tracking-wider mb-3">
              Wenn eure Annahme falsch ist, verbrennt ihr:
            </p>
            <div className="space-y-2">
              {adjustedPivotCost > 0 && (
                <div className="flex items-center gap-2 text-base text-foreground">
                  <Euro className="w-4 h-4 text-destructive flex-shrink-0" />
                  <span>
                    <span className="font-semibold">{formatCurrency(adjustedPivotCost)}</span> direkte Opportunitätskosten
                    {revMultiplier !== 1.0 && (
                      <span className="text-sm text-muted-foreground ml-1">(Rev.-Faktor {revMultiplier}x)</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-base text-foreground">
                <Clock className="w-4 h-4 text-destructive flex-shrink-0" />
                <span><span className="font-semibold">{pivotDevMonths}</span> Entwickler-Monate</span>
              </div>
              {kpiLossUntilPivot > 0 && (
                <div className="flex items-center gap-2 text-base text-foreground">
                  <TrendingDown className="w-4 h-4 text-destructive flex-shrink-0" />
                  <span><span className="font-semibold">{kpiLossUntilPivot.toFixed(0)}</span> verlorene {kpiLabel}</span>
                </div>
              )}
              {selectedLockIns.length > 0 && (
                <div className="flex items-center gap-2 text-base text-foreground">
                  <Lock className="w-4 h-4 text-destructive flex-shrink-0" />
                  <span>
                    <span className="font-semibold">{selectedLockIns.length} strategische Lock-In{selectedLockIns.length > 1 ? 's' : ''}</span>
                    {' '}({selectedLockIns.map(l => LOCK_IN_OPTIONS.find(o => o.id === l)?.label.replace('-Lock-In', '')).join(', ')})
                  </span>
                </div>
              )}
              {expectedPivotExposure > 0 && worstCaseProbability && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 pl-6">
                  <span>Wahrscheinlichkeitsgewichtet: {formatCurrency(expectedPivotExposure)}</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-3 italic">
              Bevor ihr es erkennt.
            </p>
          </div>

          {/* Commitment Gate with Checkbox */}
          <div className="p-5 rounded-xl border-2 border-border bg-card space-y-4">
            <p className="text-base font-semibold text-foreground text-center">
              Seid ihr bereit, dieses Risiko bewusst einzugehen?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant={commitDecision === 'commit' ? 'default' : 'outline'}
                onClick={() => setCommitDecision('commit')}
                className={cn(
                  "flex-1 h-12 text-base font-semibold transition-all",
                  commitDecision === 'commit' && "ring-2 ring-primary ring-offset-2"
                )}
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Ja, wir committen.
              </Button>
              <Button
                variant={commitDecision === 'reduce_risk' ? 'destructive' : 'outline'}
                onClick={() => setCommitDecision('reduce_risk')}
                className={cn(
                  "flex-1 h-12 text-base font-semibold transition-all",
                  commitDecision === 'reduce_risk' && "ring-2 ring-destructive ring-offset-2"
                )}
              >
                <ShieldAlert className="w-5 h-5 mr-2" />
                Nein, wir reduzieren Risiko.
              </Button>
            </div>

            {/* Checkbox confirmation */}
            {commitDecision && (
              <div className={cn(
                "p-4 rounded-lg border animate-in fade-in duration-200 space-y-3",
                commitDecision === 'commit'
                  ? "bg-emerald-500/10 border-emerald-500/30"
                  : "bg-amber-500/10 border-amber-500/30"
              )}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="commit-checkbox"
                    checked={commitCheckboxConfirmed}
                    onCheckedChange={(checked) => setCommitCheckboxConfirmed(checked === true)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="commit-checkbox"
                    className={cn(
                      "text-sm cursor-pointer leading-relaxed",
                      commitDecision === 'commit'
                        ? "text-emerald-700 dark:text-emerald-300"
                        : "text-amber-700 dark:text-amber-300"
                    )}
                  >
                    {commitDecision === 'commit'
                      ? `Wir akzeptieren eine Exposition von ${formatCurrency(adjustedPivotCost)} und einen Point of No Return in ${pivotCalendarMonths.toFixed(1)} Monaten. Wir akzeptieren ${selectedLockIns.length > 0 ? `${selectedLockIns.length} strategische Lock-In-Dimension${selectedLockIns.length > 1 ? 'en' : ''} und ` : ''}eine Pivot-Wahrscheinlichkeit von ${pivotProbability}%.`
                      : "Wir akzeptieren, dass Risikoreduktion unsere Validierungsdeadline verlängert und den strategischen Vorteil verzögert."
                    }
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
